import { useState } from 'react'
import { api, ApiError } from '../api'
import { setSession } from '../auth'

interface LoginProps {
    onAuthed: () => void
}

type Mode = 'login' | 'register'
// field=null 的报错挂在提交按钮上方（如登录 401「用户名或密码错误」——故意不归到某个框，防用户名枚举）
type Field = 'username' | 'password' | 'invite'
interface FieldError {
    field: Field | null
    msg: string
}

export function Login({ onAuthed }: LoginProps) {
    const [mode, setMode] = useState<Mode>('login')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [inviteCode, setInviteCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<FieldError | null>(null)

    async function submit() {
        if (busy) return
        setError(null)
        const u = username.trim()
        if (u.length < 3) {
            setError({ field: 'username', msg: '用户名至少 3 位' })
            return
        }
        if (mode === 'register') {
            if (password.length < 8) {
                setError({ field: 'password', msg: '密码至少 8 位' })
                return
            }
            if (!inviteCode.trim()) {
                setError({ field: 'invite', msg: '请输入邀请码' })
                return
            }
        }
        setBusy(true)
        try {
            const resp =
                mode === 'login'
                    ? await api.auth.login(u, password)
                    : await api.auth.register(u, password, inviteCode.trim())
            setSession({ accessToken: resp.accessToken, username: resp.username })
            onAuthed()
        } catch (err) {
            const msg = err instanceof Error ? err.message : '操作失败'
            // 注册的字段级错误按 status 归位；登录的 401 保持全局（field=null），不暴露是哪一项错
            let field: Field | null = null
            if (err instanceof ApiError && mode === 'register') {
                if (err.status === 409 || err.status === 400) field = 'username'
                else if (err.status === 403) field = 'invite'
            }
            setError({ field, msg })
        } finally {
            setBusy(false)
        }
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') submit()
    }

    const fieldErr = (f: Field) =>
        error?.field === f ? <p className="mb-3 -mt-1 text-center text-sm text-destructive">{error.msg}</p> : null

    return (
        <div className="flex h-full flex-col items-center justify-center px-8">
            <div className="animate-rise flex w-full max-w-sm flex-col items-center">
                <h1 className="text-liquid mb-3 font-display text-6xl font-semibold tracking-tight">Small Talk</h1>
                <p className="mb-12 text-sm tracking-[0.15em] text-text-secondary">
                    {mode === 'login' ? '欢迎回来' : '创建新账号'}
                </p>

                <div className="w-full">
                <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="用户名"
                    autoComplete="username"
                    autoCapitalize="off"
                    className="glass mb-3 h-12 w-full rounded-full bg-bg-secondary px-5 text-base text-text-primary transition focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
                {fieldErr('username')}
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="密码"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    className="glass mb-3 h-12 w-full rounded-full bg-bg-secondary px-5 text-base text-text-primary transition focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
                {fieldErr('password')}
                {mode === 'register' && (
                    <input
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="邀请码"
                        autoCapitalize="off"
                        autoComplete="off"
                        className="glass mb-3 h-12 w-full rounded-full bg-bg-secondary px-5 text-base text-text-primary transition focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                )}
                {mode === 'register' && fieldErr('invite')}
                {error && !error.field && <p className="mb-3 text-center text-sm text-destructive">{error.msg}</p>}
                <button
                    onClick={submit}
                    disabled={busy || !username.trim() || !password}
                    className="liquid mt-1 h-12 w-full rounded-full text-base font-semibold tracking-wide text-white shadow-lg shadow-accent/25 transition active:scale-[0.99] active:opacity-90 disabled:opacity-40"
                >
                    {busy ? '处理中…' : mode === 'login' ? '登录' : '注册并登录'}
                </button>

                <button
                    onClick={() => {
                        setMode(mode === 'login' ? 'register' : 'login')
                        setError(null)
                    }}
                    className="mt-5 w-full text-center text-sm text-text-secondary transition-colors hover:text-accent active:opacity-50"
                >
                    {mode === 'login' ? '还没账号？注册一个' : '已有账号？去登录'}
                </button>
                </div>
            </div>
        </div>
    )
}
